export interface CanonicalCity {
  city: string;
  state: string;
}

export const CANONICAL_MARKET_CITIES: CanonicalCity[] = [
  { city: "Phoenix", state: "AZ" },
  { city: "Los Angeles", state: "CA" },
  { city: "Riverside", state: "CA" },
  { city: "Sacramento", state: "CA" },
  { city: "Denver", state: "CO" },
  { city: "Washington", state: "DC" },
  { city: "Cape Coral", state: "FL" },
  { city: "Deltona", state: "FL" },
  { city: "Gainesville", state: "FL" },
  { city: "Jacksonville", state: "FL" },
  { city: "Lakeland", state: "FL" },
  { city: "Miami", state: "FL" },
  { city: "Ocala", state: "FL" },
  { city: "Orlando", state: "FL" },
  { city: "Palm Bay", state: "FL" },
  { city: "Sarasota", state: "FL" },
  { city: "Tampa", state: "FL" },
  { city: "Atlanta", state: "GA" },
  { city: "Chicago", state: "IL" },
  { city: "New Orleans", state: "LA" },
  { city: "Boston", state: "MA" },
  { city: "Baltimore", state: "MD" },
  { city: "Detroit", state: "MI" },
  { city: "Kansas City", state: "MO" },
  { city: "Charlotte", state: "NC" },
  { city: "Raleigh", state: "NC" },
  { city: "Las Vegas", state: "NV" },
  { city: "New York", state: "NY" },
  { city: "Portland", state: "OR" },
  { city: "Philadelphia", state: "PA" },
  { city: "Austin", state: "TX" },
  { city: "Dallas", state: "TX" },
  { city: "Houston", state: "TX" },
  { city: "San Antonio", state: "TX" },
  { city: "Richmond", state: "VA" },
  { city: "Seattle", state: "WA" },
];

export function canonicalCityKey(city: string, state: string): string {
  return `${city.trim().toLowerCase()}|${state.trim().toUpperCase()}`;
}

export function findCanonicalMatch(city: string, state: string): CanonicalCity | null {
  const wantKey = canonicalCityKey(city, state);
  return CANONICAL_MARKET_CITIES.find(c => canonicalCityKey(c.city, c.state) === wantKey) ?? null;
}

// ─────────────────────────────────────────────────────────────────────
// Canonical month key for market_research.data_month and
// market_research_history.month.
//
// The canonical format is the FULL month name + 4-digit year, e.g.
// "July 2026". This is what every existing row in
// market_research_history uses ("March 2025", "September 2025",
// "October 2025", "April 2026") and — critically — what the Research
// chart's month-label parser (`monthLabelToOffset` in
// client/src/pages/app-research-city.tsx) understands. A month stored
// in any other spelling (e.g. abbreviated "Jul 2026") silently
// disappears from the chart because its label can't be mapped to an
// X-axis offset.
//
// `canonicalizeDataMonth` is deliberately forgiving on INPUT (full or
// abbreviated month names, 2- or 4-digit years, "July '26"-style
// apostrophes as printed on the PadSplit Market Spot PDF header) but
// strict on OUTPUT so every writer produces the exact same key.
// ─────────────────────────────────────────────────────────────────────

const DATA_MONTH_LOOKUP: Record<string, string> = {
  jan: "January", january: "January",
  feb: "February", february: "February",
  mar: "March", march: "March",
  apr: "April", april: "April",
  may: "May",
  jun: "June", june: "June",
  jul: "July", july: "July",
  aug: "August", august: "August",
  sep: "September", sept: "September", september: "September",
  oct: "October", october: "October",
  nov: "November", november: "November",
  dec: "December", december: "December",
};

/**
 * Normalize a human-entered / PDF-extracted month label to the canonical
 * "Month YYYY" form (e.g. "July 2026"). Returns null when the label
 * cannot be recognized as a real month + year.
 *
 * Accepted inputs include: "July 2026", "Jul 2026", "july-2026",
 * "July, 2026", "July 26", "July '26" (2-digit years are read as 20xx).
 */
export function canonicalizeDataMonth(raw: string): string | null {
  const m = raw.trim().toLowerCase().match(/^([a-z]+)[\s\-,/.'’]+(\d{4}|\d{2})$/);
  if (!m) return null;
  const month = DATA_MONTH_LOOKUP[m[1]];
  if (!month) return null;
  const year = m[2].length === 2 ? 2000 + Number.parseInt(m[2], 10) : Number.parseInt(m[2], 10);
  return `${month} ${year}`;
}

const CANONICAL_MONTH_ORDER = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Chronological sort key for a data-month label (year × 12 + month index),
 * or null when the label is not a recognizable month + year. Accepts any
 * spelling `canonicalizeDataMonth` accepts, so legacy "Jul 2026" rows
 * compare equal to "July 2026". Used by the market-data backfills so a
 * re-run never moves a snapshot back to an older month.
 */
export function dataMonthSortKey(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const canonical = canonicalizeDataMonth(raw);
  if (!canonical) return null;
  const [month, year] = canonical.split(" ");
  return Number.parseInt(year, 10) * 12 + CANONICAL_MONTH_ORDER.indexOf(month);
}
